import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FaEdit, FaTrash, FaUserFriends, FaSpinner, FaCrown, FaSignOutAlt } from 'react-icons/fa';

interface OrganizationDetailsProps {
  organizationId: string;
  onOrganizationUpdated: () => void;
  onLeaveOrganization: () => void;
}

interface Organization {
  id: string;
  name: string;
  created_at: string;
  updated_at: string;
}

interface Department {
  id: string;
  name: string;
  parent_department_id: string | null;
  token_count: number;
}

interface User {
  id: string;
  name: string;
  email: string;
  role: string;
}

export default function OrganizationDetails({ 
  organizationId, 
  onOrganizationUpdated, 
  onLeaveOrganization 
}: OrganizationDetailsProps) {
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [userRole, setUserRole] = useState<string>('');
  const [userCount, setUserCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [selectedNewAdmin, setSelectedNewAdmin] = useState('');
  const [isTransferring, setIsTransferring] = useState(false);
  const [isLeaving, setIsLeaving] = useState(false);

  useEffect(() => {
    fetchOrganizationDetails();
  }, [organizationId]);

  const fetchOrganizationDetails = async () => {
    try {
      setIsLoading(true);
      const response = await axios.get(`/api/organizations/${organizationId}`);
      const data = response.data;
      
      setOrganization(data.organization);
      setDepartments(data.departments);
      setUserCount(data.user_count);
      setUserRole(data.user_role);
      setEditName(data.organization.name);
      
      // Fetch users
      const usersResponse = await axios.get(`/api/organizations/${organizationId}/users`);
      setUsers(usersResponse.data.users);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to fetch organization details');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveEdit = async () => {
    if (!editName.trim()) return;
    
    setIsSaving(true);
    try {
      await axios.put(`/api/organizations/${organizationId}`, {
        name: editName.trim()
      });
      
      setOrganization(prev => prev ? { ...prev, name: editName.trim() } : null);
      setIsEditing(false);
      onOrganizationUpdated();
      
      // Emit event to notify other components
      window.dispatchEvent(new CustomEvent('organizationChanged'));
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update organization');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteOrganization = async () => {
    if (!window.confirm('Are you sure you want to delete this organization? This action cannot be undone.')) {
      return;
    }
    
    try {
      await axios.delete(`/api/organizations/${organizationId}`);
      onLeaveOrganization();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to delete organization');
    }
  };

  const handleTransferOwnership = async () => {
    if (!selectedNewAdmin) return;
    
    setIsTransferring(true);
    try {
      await axios.post(`/api/organizations/${organizationId}/transfer-ownership`, {
        new_admin_user_id: selectedNewAdmin
      });
      
      setShowTransferModal(false);
      setSelectedNewAdmin('');
      fetchOrganizationDetails();
      onOrganizationUpdated();
      
      // Emit event to notify other components
      window.dispatchEvent(new CustomEvent('organizationChanged'));
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to transfer ownership');
    } finally {
      setIsTransferring(false);
    }
  };

  const handleLeaveOrganization = async () => {
    if (!window.confirm('Are you sure you want to leave this organization?')) {
      return;
    }
    
    setIsLeaving(true);
    try {
      await axios.post(`/api/organizations/${organizationId}/leave`);
      onLeaveOrganization();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to leave organization');
    } finally {
      setIsLeaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <FaSpinner className="animate-spin h-8 w-8 text-indigo-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-md p-4">
        <div className="text-red-800">{error}</div>
      </div>
    );
  }

  if (!organization) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
        <div className="text-yellow-800">Organization not found</div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-md">
      <div className="p-6 border-b border-gray-200">
        <div className="flex justify-between items-start">
          <div>
            {isEditing ? (
              <div className="flex items-center space-x-2">
                <input
                  type="text"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  className="text-2xl font-bold text-gray-900 border border-gray-300 rounded px-2 py-1"
                />
                <button
                  onClick={handleSaveEdit}
                  disabled={isSaving}
                  className="text-green-600 hover:text-green-800"
                >
                  {isSaving ? <FaSpinner className="animate-spin h-4 w-4" /> : 'Save'}
                </button>
                <button
                  onClick={() => {
                    setIsEditing(false);
                    setEditName(organization.name);
                  }}
                  className="text-gray-600 hover:text-gray-800"
                >
                  Cancel
                </button>
              </div>
            ) : (
              <div className="flex items-center space-x-2">
                <h1 className="text-2xl font-bold text-gray-900">{organization.name}</h1>
                {userRole === 'admin' && (
                  <button
                    onClick={() => setIsEditing(true)}
                    className="text-gray-600 hover:text-gray-800"
                  >
                    <FaEdit className="h-4 w-4" />
                  </button>
                )}
              </div>
            )}
            <p className="text-gray-600 mt-1">
              Created on {new Date(organization.created_at).toLocaleDateString()}
            </p>
          </div>
          
          <div className="flex space-x-2">
            {userRole === 'admin' && (
              <>
                <button
                  onClick={() => setShowTransferModal(true)}
                  className="flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  <FaCrown className="h-4 w-4 mr-1" />
                  Transfer Ownership
                </button>
                <button
                  onClick={handleDeleteOrganization}
                  className="flex items-center px-3 py-2 text-sm font-medium text-red-700 bg-white border border-red-300 rounded-md hover:bg-red-50"
                >
                  <FaTrash className="h-4 w-4 mr-1" />
                  Delete Organization
                </button>
              </>
            )}
            <button
              onClick={handleLeaveOrganization}
              disabled={isLeaving}
              className="flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
            >
              {isLeaving ? (
                <FaSpinner className="animate-spin h-4 w-4 mr-1" />
              ) : (
                <FaSignOutAlt className="h-4 w-4 mr-1" />
              )}
              Leave Organization
            </button>
          </div>
        </div>
      </div>

      <div className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="flex items-center">
              <FaUserFriends className="h-5 w-5 text-indigo-600 mr-2" />
              <h3 className="text-lg font-medium text-gray-900">Members</h3>
            </div>
            <p className="text-3xl font-bold text-indigo-600 mt-2">{userCount}</p>
          </div>
          
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-gray-900">Departments</h3>
            <p className="text-3xl font-bold text-indigo-600 mt-2">{departments.length}</p>
          </div>
          
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-gray-900">Your Role</h3>
            <p className="text-lg font-semibold text-indigo-600 mt-2 capitalize">{userRole}</p>
          </div>
        </div>

        <div className="mt-8">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Departments</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {departments.map((dept) => (
              <div key={dept.id} className="bg-white border border-gray-200 rounded-lg p-4">
                <h4 className="font-medium text-gray-900">{dept.name}</h4>
                <p className="text-sm text-gray-600 mt-1">
                  Token Count: {dept.token_count}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Transfer Ownership Modal */}
      {showTransferModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Transfer Ownership</h3>
              <select
                value={selectedNewAdmin}
                onChange={(e) => setSelectedNewAdmin(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value="">Select a new admin</option>
                {users
                  .filter(user => user.id !== organization.id && user.role !== 'admin')
                  .map(user => (
                    <option key={user.id} value={user.id}>
                      {user.name} ({user.email})
                    </option>
                  ))}
              </select>
              
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => setShowTransferModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleTransferOwnership}
                  disabled={!selectedNewAdmin || isTransferring}
                  className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 disabled:bg-indigo-400"
                >
                  {isTransferring ? 'Transferring...' : 'Transfer Ownership'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
} 